import React, { useEffect, useState, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { RadioButton } from 'primereact/radiobutton';
import { Button } from 'primereact/button';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { confirmDialog } from 'primereact/confirmdialog';
import { useGetChassisListForPSIQuery, useCheckVafExistsQuery, useSentToKarsanMutation } from '../../../../../../api';
import useQuestionsAnswersData from '../hook/useQuestionsAnswersData';
import { toastService } from '../../../../../../utils/toastService';
import { useNavigate } from 'react-router-dom';
import ButtonGroup from '../../../../../../components/ButtonGroup/ButtonGroup';
import { PSIStatusEnum, PSIAnswer } from '../../hook/usePreStockInspectionData';
import { useLanguage } from '../../../../../../contexts/LanguageContext';
import { formatDateForApi } from '../../../../../../utils/dateUtils';
import ChassisDropdown from '../../../shared/components/ChassisDropdown';
import DateChangeRequestField from '../../../shared/components/DateChangeRequestField';
import { preparePreStockInspectionPayload } from '../utils/preparePreStockInspectionPayload';
import { FORM_TYPE, DATE_FIELD } from '../../../../../../utils/formDateChangeConstants';

const PSI_LIST_ROUTE = '/warranty-management/warranty-process-forms/pre-stock-inspection-checklist';

const FormPreStockInspection = ({ editMode, id, submitted, formData, handleChange, savePreStockInspection, resetForm, setSubmitted, setFormData }) => {
    const navigate = useNavigate();
    const { th } = useLanguage();
    const { questionsWithAnswers, inspectionInfo, isLoading: isLoadingQuestions } = useQuestionsAnswersData(id, editMode);
    const {
        data: vafResult,
        isFetching,
        error: vafError
    } = useCheckVafExistsQuery(formData.vin, {
        skip: !formData.vin
    });

    const [sendToKarsan, { isLoading: isSending }] = useSentToKarsanMutation();

    const [vinErrorMessage, setVinErrorMessage] = useState('');

    const [initialFormData, setInitialFormData] = useState({});

    const [searchTerm, setSearchTerm] = useState('');
    const checklistRefs = useRef([]);

    const [commentErrors, setCommentErrors] = useState([]);

    const disableControlForm = editMode && inspectionInfo?.status === PSIStatusEnum.SENDTOKARSAN;

    useEffect(() => {
        if (!Array.isArray(formData.answers)) return;

        const newErrors = formData.answers.map((a) => a.answer === PSIAnswer.NOK && (!a.comment || a.comment.trim() === ''));

        setCommentErrors(newErrors);
    }, [formData.answers]);

    useEffect(() => {
        const formIsReady = formData?.vin && questionsWithAnswers.length > 0 && (!editMode || inspectionInfo !== null);

        if (formIsReady && Object.keys(initialFormData).length === 0) {
            setInitialFormData(normalizeFormData(formData));
        }
    }, [formData.vin, questionsWithAnswers, inspectionInfo, editMode]);

    useEffect(() => {
        if (!formData.vin) {
            setVinErrorMessage('');

            return;
        }

        if (vafError) {
            const message = th('warranty.preStockInspection.messages.vafCheckServerError');
            setVinErrorMessage(message);
            toastService.error(message);

            return;
        }

        if (vafResult) {
            if (!vafResult.isSuccess) {
                const message = vafResult.errorMessage || th('warranty.preStockInspection.messages.vafNotFound');
                setVinErrorMessage(message);
                toastService.error(message);
            } else {
                setVinErrorMessage('');
            }
        }
    }, [formData.vin, vafResult, vafError, th]);

    const normalizeFormData = (form) => {
        return {
            ...form,
            stockInDate: formatDateForApi(form.stockInDate),
            inspectionDate: formatDateForApi(form.inspectionDate)
        };
    };
    const hasUnsavedChanges = () => {
        const current = normalizeFormData(formData);
        const initial = normalizeFormData(initialFormData);
        //Farkları consola yazsın diye eklenen bir fonksiyon kaldırılabili
        // diffForms(formData, initialFormData);
        return JSON.stringify(current) !== JSON.stringify(initial);
    };

    // handleSave ve handleSendToKarsan aynı zorunlu alan / VIN / NOK-yorum kontrollerini
    // yapıyordu (iki yerde birebir kopya). Artık tek yerde yaşıyor.
    const validateForm = () => {
        if (!formData.vin || !formData.inspectorName || !formData.stockInDate || !formData.inspectionDate) {
            return false;
        }

        // VIN kontrolü
        if (vinErrorMessage || !vafResult?.exists) {
            toastService.error(vinErrorMessage || th('warranty.preStockInspection.messages.vafCheckFailed'));
            return false;
        }

        // NOK + yorum kontrolü
        const errors = Array.isArray(formData.answers) ? formData.answers.map((a) => a.answer === PSIAnswer.NOK && (!a.comment || a.comment.trim() === '')) : [];
        setCommentErrors(errors);

        if (errors.includes(true)) {
            return false;
        }

        return true;
    };

    const handleSave = async () => {
        setSubmitted(true);
        if (!validateForm()) return;

        const payload = preparePreStockInspectionPayload(formData, editMode, inspectionInfo, questionsWithAnswers);
        const isThereAnyChanges = hasUnsavedChanges();

        const success = await savePreStockInspection(payload, editMode, isThereAnyChanges);
        if (success) {
            setInitialFormData(normalizeFormData(formData));
            navigate(PSI_LIST_ROUTE);
        }
    };

    const handleSendToKarsan = async () => {
        setSubmitted(true);
        if (!validateForm()) return;

        const payload = preparePreStockInspectionPayload(formData, editMode, inspectionInfo, questionsWithAnswers);
        try {
            const isThereAnyChanges = hasUnsavedChanges();

            if (isThereAnyChanges) {
                const success = await savePreStockInspection(payload, editMode, isThereAnyChanges);

                if (!success) {
                    toastService.error(th('warranty.preStockInspection.messages.saveFailed'));
                    return;
                }
            }

            const result = await sendToKarsan(id || payload.id).unwrap();

            if (result?.isSuccessful) {
                toastService.success(th('warranty.preStockInspection.messages.sentToKarsanSuccess'));
                setInitialFormData(normalizeFormData(formData));
                navigate(PSI_LIST_ROUTE);
            } else {
                toastService.error(result?.errorMessage || th('warranty.preStockInspection.messages.sendFailed'));
            }
        } catch (error) {
            toastService.error(error?.data?.message || th('warranty.preStockInspection.messages.serverError'));
        }
    };

    const handleExit = () => {
        if (editMode)
            if (hasUnsavedChanges()) {
                confirmDialog({
                    message: th('warranty.preStockInspection.messages.unsavedChangesConfirm'),
                    header: th('common.warning'),
                    icon: 'pi pi-exclamation-triangle',
                    acceptLabel: th('common.yes'),
                    rejectLabel: th('common.no'),
                    accept: () => navigate(PSI_LIST_ROUTE),
                    rejectClassName: 'p-button-outlined'
                });
            } else {
                navigate(PSI_LIST_ROUTE);
            }
        else {
            navigate(PSI_LIST_ROUTE);
        }
    };
    const handleChecklistChange = (index, key, value) => {
        const updatedAnswers = [...formData.answers];
        updatedAnswers[index] = { ...updatedAnswers[index], [key]: value };
        handleChange('answers', updatedAnswers);
    };

    useEffect(() => {
        if (editMode && inspectionInfo) {
            setFormData({
                vin: inspectionInfo.vin,
                inspectorName: inspectionInfo.inspectorName,
                inspectorTitle: inspectionInfo.inspectorTitle,
                inspectionDate: new Date(inspectionInfo.inspectionDate),
                stockInDate: new Date(inspectionInfo.stockInDate),
                answers: questionsWithAnswers
            });
        }
    }, [inspectionInfo, questionsWithAnswers]);

    useEffect(() => {
        if (!formData.answers?.length && questionsWithAnswers.length) {
            handleChange('answers', questionsWithAnswers);
        }
    }, [questionsWithAnswers]);

    const handleSearch = () => {
        const index = questionsWithAnswers.findIndex((q) => q.questionText.toLowerCase().includes(searchTerm.toLowerCase()));
        if (index !== -1 && checklistRefs.current[index]) {
            checklistRefs.current[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const highlightText = (text, searchTerm) => {
        if (!searchTerm) return text;

        const regex = new RegExp(`(${searchTerm})`, 'gi');
        const parts = text.split(regex);

        return parts.map((part, i) =>
            part.toLowerCase() === searchTerm.toLowerCase() ? (
                <span key={i} style={{ backgroundColor: 'yellow' }}>
                    {part}
                </span>
            ) : (
                part
            )
        );
    };

    return (
        <div className="grid">
            <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: '0px' }}>
                    <label htmlFor="vin" className="font-bold block">
                        {th('warranty.preStockInspection.form.vin')}
                    </label>
                    <ChassisDropdown
                        useChassisQuery={useGetChassisListForPSIQuery}
                        placeholderKey="warranty.preStockInspection.form.placeholders.vin"
                        disabled={disableControlForm}
                        value={formData.vin}
                        onChange={(value) => handleChange('vin', value)}
                        className={(submitted && !formData.vin) || vinErrorMessage ? 'p-invalid w-full' : 'w-full'}
                    />
                    {submitted && !formData.vin && <small className="p-error">{th('warranty.preStockInspection.form.validation.vinRequired')}</small>}
                    {vinErrorMessage && <small className="p-error">{vinErrorMessage}</small>}{' '}
                </div>
            </div>
            <div className="col-12 md:col-6">
                <div className="field ">
                    <label htmlFor="stockInDate" className="font-bold block">
                        {th('warranty.preStockInspection.form.stockInDate')}
                    </label>
                    {editMode && disableControlForm ? (
                        <DateChangeRequestField formType={FORM_TYPE.PSI} dateField={DATE_FIELD.STOCK_IN_DATE} formId={inspectionInfo?.id || Number(id)} currentDate={formData.stockInDate} label={th('warranty.preStockInspection.form.stockInDate')} />
                    ) : (
                        <>
                            <Calendar
                                disabled={disableControlForm}
                                id="stockInDate"
                                value={formData.stockInDate}
                                onChange={(e) => handleChange('stockInDate', e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                className={submitted && !formData.stockInDate ? 'p-invalid w-full' : 'w-full'}
                                placeholder={th('warranty.preStockInspection.form.placeholders.stockInDate')}
                            />
                            {submitted && !formData.stockInDate && <small className="p-error">{th('warranty.preStockInspection.form.validation.stockInDateRequired')}</small>}
                        </>
                    )}
                </div>
            </div>
            <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: '0px' }}>
                    <label htmlFor="inspectorName" className="font-bold block">
                        {th('warranty.preStockInspection.form.inspectorName')}
                    </label>
                    <InputText disabled={disableControlForm} value={formData.inspectorName} onChange={(e) => handleChange('inspectorName', e.target.value)} className={submitted && !formData.inspectorName ? 'p-invalid w-full' : 'w-full'} />
                    {submitted && !formData.inspectorName && <small className="p-error">{th('warranty.preStockInspection.form.validation.inspectorNameRequired')}</small>}
                </div>
            </div>

            <div className="col-12 md:col-6">
                <div className="field ">
                    <label htmlFor="inspectionDate" className="font-bold block">
                        {th('warranty.preStockInspection.form.inspectionDate')}
                    </label>
                    {editMode && disableControlForm ? (
                        <DateChangeRequestField
                            formType={FORM_TYPE.PSI}
                            dateField={DATE_FIELD.INSPECTION_DATE}
                            formId={inspectionInfo?.id || Number(id)}
                            currentDate={formData.inspectionDate}
                            label={th('warranty.preStockInspection.form.inspectionDate')}
                        />
                    ) : (
                        <>
                            <Calendar
                                disabled={disableControlForm}
                                id="inspectionDate"
                                value={formData.inspectionDate}
                                onChange={(e) => handleChange('inspectionDate', e.value)}
                                dateFormat="dd/mm/yy"
                                showIcon
                                className={submitted && !formData.inspectionDate ? 'p-invalid w-full' : 'w-full'}
                                placeholder={th('warranty.preStockInspection.form.placeholders.inspectionDate')}
                            />
                            {submitted && !formData.inspectionDate && <small className="p-error">{th('warranty.preStockInspection.form.validation.inspectionDateRequired')}</small>}
                        </>
                    )}
                </div>
            </div>
            <div className="col-12 md:col-6">
                <div className="field" style={{ marginBottom: '0px' }}>
                    <label htmlFor="inspectorTitle" className="font-bold block">
                        {th('warranty.preStockInspection.form.inspectorTitle')}
                    </label>
                    <InputText disabled={disableControlForm} className="w-full" value={formData.inspectorTitle} onChange={(e) => handleChange('inspectorTitle', e.target.value)} />
                </div>
            </div>

            <div className="col-12 md:col-12 my-2 border-top-1 border-300" />
            <div className="col-12 s">
                <h4 className="font-bold">{th('warranty.preStockInspection.form.checklistTitle')}</h4>

                <div className="field mb-6" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem' }}>
                    <InputText
                        placeholder={th('warranty.preStockInspection.form.placeholders.searchQuestion')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                        style={{ width: '50%' }}
                    />
                    <Button label={th('warranty.preStockInspection.buttons.find')} onClick={handleSearch} />
                </div>

                {questionsWithAnswers.map((item, index) => (
                    <div key={item.questionId} ref={(el) => (checklistRefs.current[index] = el)} className="grid align-items-center mb-1">
                        <div className="col-12 lg:col-6">
                            <label>
                                {index + 1}) {highlightText(item.questionText, searchTerm)}
                            </label>
                        </div>

                        <div className="col-4 lg:col-2">
                            <RadioButton
                                disabled={disableControlForm}
                                inputId={`ok-${item.questionId}`}
                                name={`check-${item.questionId}`}
                                value={PSIAnswer.OK}
                                onChange={(e) => handleChecklistChange(index, 'answer', e.value)}
                                checked={formData.answers?.[index]?.answer === PSIAnswer.OK}
                            />
                            <label htmlFor={`ok-${item.questionId}`} className="ml-2">
                                {th('warranty.preStockInspection.form.ok')}
                            </label>
                        </div>

                        <div className="col-4 lg:col-2">
                            <RadioButton
                                disabled={disableControlForm}
                                inputId={`nok-${item.questionId}`}
                                name={`check-${item.questionId}`}
                                value={PSIAnswer.NOK}
                                onChange={(e) => handleChecklistChange(index, 'answer', e.value)}
                                checked={formData.answers?.[index]?.answer === PSIAnswer.NOK}
                            />
                            <label htmlFor={`nok-${item.questionId}`} className="ml-2">
                                {th('warranty.preStockInspection.form.nok')}
                            </label>
                        </div>

                        <div className="col-12 lg:col-2">
                            <InputText
                                disabled={disableControlForm}
                                placeholder={th('warranty.preStockInspection.form.placeholders.comment')}
                                value={formData.answers?.[index]?.comment || ''}
                                onChange={(e) => handleChecklistChange(index, 'comment', e.target.value)}
                                className={(commentErrors[index] ? 'p-invalid' : '') + ' w-full'}
                            />
                            {commentErrors && commentErrors[index] && <small className="p-error">{th('warranty.preStockInspection.form.validation.nokCommentRequired')}</small>}
                        </div>
                    </div>
                ))}
            </div>

            <div className="col-12 md:col-12" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                {editMode && inspectionInfo?.status === PSIStatusEnum.DRAFT && (
                    <ButtonGroup
                        buttons={[
                            {
                                label: th('warranty.preStockInspection.buttons.save'),
                                onClick: handleSave,
                                severity: 'success'
                            },
                            {
                                label: th('warranty.preStockInspection.buttons.send'),
                                onClick: handleSendToKarsan,
                                severity: 'gray',
                                disabled: isSending
                            },
                            {
                                label: th('warranty.preStockInspection.buttons.exit'),
                                onClick: handleExit,
                                severity: 'danger'
                            }
                        ]}
                    />
                )}

                {editMode && inspectionInfo?.status === PSIStatusEnum.SENDTOKARSAN && (
                    <ButtonGroup
                        buttons={[
                            {
                                label: th('warranty.preStockInspection.buttons.exit'),
                                onClick: handleExit,
                                severity: 'danger'
                            }
                        ]}
                    />
                )}

                {!editMode && (
                    <ButtonGroup
                        buttons={[
                            {
                                label: th('warranty.preStockInspection.buttons.save'),
                                onClick: handleSave,
                                severity: 'success'
                            },
                            {
                                label: th('warranty.preStockInspection.buttons.exit'),
                                onClick: handleExit,
                                severity: 'danger'
                            }
                        ]}
                    />
                )}
            </div>
            <ConfirmDialog />
        </div>
    );
};

export default FormPreStockInspection;
